import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

function AnimatedSection({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold: 0.06 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(32px)', transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s` }}>
      {children}
    </div>
  );
}

const VALUES = [
  {
    icon: 'ri-lightbulb-flash-line',
    title: 'Głęboka Ekspertyza',
    desc: 'Pracujemy wyłącznie z technologiami, które sami dogłębnie rozumiemy. Zero gotowych szablonów, zero copy-paste. Każde wdrożenie jest projektowane od podstaw z myślą o konkretnym biznesie i jego realnych celach.',
    color: '#00d4ff',
  },
  {
    icon: 'ri-bar-chart-2-line',
    title: 'Mierzalne Wyniki',
    desc: 'Nie sprzedajemy ładnych prezentacji. Każdy projekt zaczyna się od KPIs i kończy raportem z faktycznych wyników — wzrostem rezerwacji, redukcją kosztów obsługi lub widocznymi pozycjami w Google.',
    color: '#00ff88',
  },
  {
    icon: 'ri-handshake-line',
    title: 'Partnerstwo Długoterminowe',
    desc: 'Traktujemy każdego klienta jak strategicznego partnera, nie jednorazowe zlecenie. Większość naszych relacji trwa latami, bo wiemy że trwały wzrost to efekt ciągłej pracy, nie jednorazowego wdrożenia.',
    color: '#ff6b9d',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Pełna Przejrzystość',
    desc: 'Transparentność na każdym etapie — od pierwszej wyceny, przez postępy w projekcie, aż po miesięczne raporty wyników. Zawsze wiesz, co robimy, dlaczego i jakie to przynosi efekty.',
    color: '#00d4ff',
  },
];

const TECH_STACK = [
  { name: 'React / Next.js', icon: 'ri-reactjs-line', desc: 'Frontendy klasy enterprise', color: '#00d4ff' },
  { name: 'OpenAI / GPT-4', icon: 'ri-robot-2-line', desc: 'Agenci AI i automatyzacje', color: '#00ff88' },
  { name: 'Google Analytics', icon: 'ri-line-chart-line', desc: 'Śledzenie i analityka', color: '#ff6b9d' },
  { name: 'Supabase', icon: 'ri-database-2-line', desc: 'Backend i bazy danych', color: '#00d4ff' },
  { name: 'Vercel / Cloud', icon: 'ri-cloud-line', desc: 'Hosting edge computing', color: '#00ff88' },
  { name: 'Google Ads', icon: 'ri-advertisement-line', desc: 'Kampanie i remarketing', color: '#ff6b9d' },
  { name: 'Figma / Design', icon: 'ri-pen-nib-line', desc: 'UI/UX i Design Systems', color: '#00d4ff' },
  { name: 'Make / n8n', icon: 'ri-flow-chart', desc: 'Automatyzacje workflow', color: '#00ff88' },
  { name: 'Tailwind CSS', icon: 'ri-css3-line', desc: 'Utility-first styling', color: '#ff6b9d' },
];

const CERTIFICATIONS = [
  { name: 'Google Partner', desc: 'Certyfikowany partner Google Ads i Analytics', icon: 'ri-google-line', color: '#00d4ff' },
  { name: 'Meta Business', desc: 'Certyfikacja Meta Business Suite', icon: 'ri-facebook-circle-line', color: '#00ff88' },
  { name: 'AI Practitioner', desc: 'Wdrożenia LLM i agentów AI', icon: 'ri-brain-line', color: '#ff6b9d' },
  { name: 'EUDR Compliance', desc: 'Specjalizacja w systemach zgodności EUDR', icon: 'ri-leaf-line', color: '#00d4ff' },
];

const MILESTONES = [
  { year: '2018', title: 'Założenie Orsonet', desc: 'Start jako agencja SEO i stron WWW dla branży turystycznej w Bieszczadach. Pierwsze projekty dla lokalnych obiektów noclegowych.', color: '#00d4ff' },
  { year: '2020', title: 'Premium & Ekspansja', desc: 'Pierwsze realizacje dla sieci obiektów noclegowych. Wdrożenie procesów produktowego designu i brand strategy.', color: '#00ff88' },
  { year: '2022', title: 'AI First', desc: 'Pierwsze wdrożenia agentów AI dla klientów. Integracja chatbotów, automatyzacji rezerwacji i obsługi klienta.', color: '#ff6b9d' },
  { year: '2023', title: 'EUDR & Compliance', desc: 'Rozszerzenie o projekty AI dla sektora regulowanego — systemy zgodności EUDR wspomagane sztuczną inteligencją.', color: '#00d4ff' },
  { year: '2024', title: '60+ Klientów', desc: 'Przekroczenie 60 aktywnych wdrożeń. Otwarcie działu szkoleń z AI dla firm. Ekspansja poza Bieszczady.', color: '#00ff88' },
  { year: '2026', title: 'Nowy Rozdział', desc: 'Pełne portfolio usług AI — od agentów, przez dedykowane oprogramowanie, aż po systemy klasy enterprise.', color: '#ff6b9d' },
];

const INDUSTRIES = [
  { name: 'Turystyka & Noclegi', icon: 'ri-hotel-line', count: '30+', color: '#00d4ff' },
  { name: 'Gastronomia', icon: 'ri-restaurant-line', count: '12+', color: '#00ff88' },
  { name: 'Zdrowie & Beauty', icon: 'ri-heart-pulse-line', count: '8+', color: '#ff6b9d' },
  { name: 'Przemysł & B2B', icon: 'ri-building-4-line', count: '10+', color: '#00d4ff' },
  { name: 'E-commerce', icon: 'ri-shopping-cart-2-line', count: '6+', color: '#00ff88' },
  { name: 'EUDR & Compliance', icon: 'ri-file-shield-2-line', count: '5+', color: '#ff6b9d' },
];

export default function ONasPage() {
  return (
    <div className="min-h-screen bg-[#080808] text-white overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section className="pt-40 pb-24 px-4 md:px-6 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(0,212,255,0.08) 0%, transparent 60%)' }}
        />
        <div className="absolute inset-0 opacity-[0.025] pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(rgba(0,212,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.6) 1px, transparent 1px)',
            backgroundSize: '70px 70px',
          }}
        />
        <div className="max-w-[1100px] mx-auto text-center relative z-10">
          <AnimatedSection>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00d4ff]/30 bg-[#00d4ff]/5 backdrop-blur-sm mb-8">
              <span className="w-2 h-2 rounded-full bg-[#00d4ff] animate-pulse" />
              <span className="text-[#00d4ff] text-xs font-semibold tracking-widest uppercase">O nas</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black leading-tight mb-8">
              Wdrażamy AI,<br />
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg, #00d4ff, #00ff88)' }}>
                dostarczamy wyniki
              </span>
            </h1>
            <p className="text-xl text-white/45 max-w-3xl mx-auto leading-relaxed">
              Orsonet to studio technologiczne specjalizujące się we wdrożeniach nowoczesnych technologii AI dla małych i średnich firm. Łączymy głęboką ekspertyzę techniczną z praktycznym podejściem biznesowym — bez buzzwordów, z konkretnymi wynikami.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 px-4 md:px-6 bg-[#060606] border-y border-white/5">
        <div className="max-w-[1200px] mx-auto">
          <AnimatedSection>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <p className="text-[#00d4ff] text-xs uppercase tracking-[3px] font-semibold mb-5">Nasza misja</p>
                <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">
                  Demokratyzacja AI<br />dla polskiego biznesu
                </h2>
                <p className="text-white/45 text-base leading-relaxed mb-6">
                  Wierzymy, że zaawansowane technologie AI nie powinny być dostępne wyłącznie dla korporacji z miliardowymi budżetami. Małe hotele, restauracje, lokalne firmy usługowe — każdy zasługuje na narzędzia, które zwiększają efektywność i pozwalają konkurować na wyższym poziomie.
                </p>
                <p className="text-white/45 text-base leading-relaxed">
                  Od 2018 roku budujemy mosty między możliwościami nowoczesnej technologii a realnym zastosowaniem w biznesie. Nie sprzedajemy hype'u. Wdrażamy rozwiązania, które mierzalnie zmieniają wyniki.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { val: '60+', label: 'Aktywnych klientów', color: '#00d4ff' },
                  { val: '8 lat', label: 'Na rynku digital', color: '#00ff88' },
                  { val: '95%', label: 'Klientów powraca', color: '#ff6b9d' },
                  { val: '24/7', label: 'Agenci AI non-stop', color: '#00d4ff' },
                ].map((s) => (
                  <div key={s.label} className="p-7 rounded-2xl border border-white/8 bg-white/[0.02] text-center hover:border-white/16 transition-all">
                    <div className="text-4xl font-black mb-2" style={{ color: s.color }}>{s.val}</div>
                    <p className="text-white/40 text-xs leading-relaxed">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Values */}
      <section className="py-32 px-4 md:px-6">
        <div className="max-w-[1400px] mx-auto">
          <AnimatedSection>
            <div className="text-center mb-16">
              <p className="text-[#00ff88] text-xs uppercase tracking-[3px] font-semibold mb-4">Nasze wartości</p>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-4">Zasady, które nami kierują</h2>
              <p className="text-white/40 text-lg max-w-2xl mx-auto">
                Nie slogany z prezentacji. Zasady stosowane w każdym projekcie, każdego dnia.
              </p>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {VALUES.map((v, idx) => (
              <AnimatedSection key={v.title} delay={idx * 0.1}>
                <div className="p-10 rounded-3xl border border-white/8 bg-white/[0.02] hover:border-white/16 transition-all duration-300 h-full">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-7"
                    style={{ background: `${v.color}12`, border: `1px solid ${v.color}30` }}>
                    <i className={`${v.icon} text-3xl`} style={{ color: v.color }} />
                  </div>
                  <h3 className="text-2xl font-black text-white mb-4">{v.title}</h3>
                  <p className="text-white/45 leading-relaxed">{v.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-24 px-4 md:px-6 bg-[#060606] border-y border-white/5">
        <div className="max-w-[1400px] mx-auto">
          <AnimatedSection>
            <div className="text-center mb-14">
              <p className="text-[#00d4ff] text-xs uppercase tracking-[3px] font-semibold mb-4">Branże</p>
              <h2 className="text-4xl font-black text-white mb-4">Specjalizujemy się w</h2>
              <p className="text-white/40 text-base max-w-2xl mx-auto">Ponad 8 lat doświadczeń w konkretnych branżach pozwala nam rozumieć wyzwania klientów lepiej niż generalistyczne agencje.</p>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
            {INDUSTRIES.map((ind, idx) => (
              <AnimatedSection key={ind.name} delay={idx * 0.07}>
                <div className="p-6 rounded-2xl border border-white/8 bg-white/[0.02] hover:border-white/18 transition-all duration-300 text-center group hover:-translate-y-1">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl mx-auto mb-4"
                    style={{ background: `${ind.color}12`, border: `1px solid ${ind.color}25` }}>
                    <i className={`${ind.icon} text-2xl`} style={{ color: ind.color }} />
                  </div>
                  <div className="text-2xl font-black mb-1" style={{ color: ind.color }}>{ind.count}</div>
                  <p className="text-white/40 text-xs leading-tight">{ind.name}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-32 px-4 md:px-6">
        <div className="max-w-[1400px] mx-auto">
          <AnimatedSection>
            <div className="text-center mb-16">
              <p className="text-[#ff6b9d] text-xs uppercase tracking-[3px] font-semibold mb-4">Technologie</p>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-4">Stack technologiczny</h2>
              <p className="text-white/40 text-lg max-w-2xl mx-auto">
                Używamy najnowocześniejszych narzędzi i technologii, które pozwalają dostarczać projekty najwyższej klasy.
              </p>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {TECH_STACK.map((t, idx) => (
              <AnimatedSection key={t.name} delay={idx * 0.07}>
                <div className="flex items-center gap-5 p-6 rounded-2xl border border-white/8 bg-white/[0.02] hover:border-white/16 transition-all duration-300 group hover:-translate-y-0.5">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{ background: `${t.color}12`, border: `1px solid ${t.color}25` }}>
                    <i className={`${t.icon} text-2xl`} style={{ color: t.color }} />
                  </div>
                  <div>
                    <div className="font-bold text-white text-base group-hover:text-[#00d4ff] transition-colors">{t.name}</div>
                    <div className="text-white/35 text-xs mt-0.5">{t.desc}</div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 px-4 md:px-6 bg-[#060606] border-y border-white/5">
        <div className="max-w-[1200px] mx-auto">
          <AnimatedSection>
            <div className="text-center mb-12">
              <p className="text-[#00d4ff] text-xs uppercase tracking-[3px] font-semibold mb-4">Certyfikaty & Partnerstwa</p>
              <h2 className="text-3xl md:text-4xl font-black text-white">Potwierdzona ekspertyza</h2>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {CERTIFICATIONS.map((c, idx) => (
              <AnimatedSection key={c.name} delay={idx * 0.09}>
                <div className="p-8 rounded-2xl border border-white/8 bg-white/[0.02] hover:border-white/18 transition-all duration-300 text-center">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5"
                    style={{ background: `${c.color}12`, border: `1px solid ${c.color}30` }}>
                    <i className={`${c.icon} text-3xl`} style={{ color: c.color }} />
                  </div>
                  <h4 className="font-black text-white text-base mb-2">{c.name}</h4>
                  <p className="text-white/35 text-xs leading-relaxed">{c.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-32 px-4 md:px-6">
        <div className="max-w-[1000px] mx-auto">
          <AnimatedSection>
            <div className="text-center mb-16">
              <p className="text-[#ff6b9d] text-xs uppercase tracking-[3px] font-semibold mb-4">Historia</p>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-4">Nasza droga</h2>
              <p className="text-white/40 text-lg">Od lokalnej agencji SEO do lidera wdrożeń AI dla polskiego biznesu.</p>
            </div>
          </AnimatedSection>
          <div className="relative">
            <div className="absolute left-[19px] md:left-1/2 top-0 bottom-0 w-px bg-white/8" />
            <div className="space-y-10">
              {MILESTONES.map((m, idx) => (
                <AnimatedSection key={m.year} delay={idx * 0.08}>
                  <div className={`flex gap-8 items-start ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      <div className="inline-block p-6 rounded-2xl border border-white/8 bg-white/[0.02] hover:border-white/16 transition-all">
                        <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: m.color }}>{m.year}</p>
                        <h3 className="text-lg font-black text-white mb-2">{m.title}</h3>
                        <p className="text-white/45 text-sm leading-relaxed">{m.desc}</p>
                      </div>
                    </div>
                    <div className="relative flex-shrink-0 w-10 flex items-start justify-center" style={{ paddingTop: '24px' }}>
                      <div className="w-5 h-5 rounded-full border-2 z-10 relative bg-[#080808]"
                        style={{ borderColor: m.color, boxShadow: `0 0 12px ${m.color}50` }} />
                    </div>
                    <div className="flex-1 hidden md:block" />
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 md:px-6 bg-[#060606] border-t border-white/5">
        <div className="max-w-[900px] mx-auto text-center">
          <AnimatedSection>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">Chcesz pracować<br />z naszym zespołem?</h2>
            <p className="text-white/40 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
              Umów bezpłatną konsultację. Bez zobowiązań — po 30 minutach będziesz wiedzieć, co konkretnie możemy zrobić dla Twojego biznesu.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center gap-2 text-black px-10 py-5 rounded-xl font-bold text-lg whitespace-nowrap cursor-pointer hover:scale-105 transition-transform"
                style={{ background: 'linear-gradient(135deg, #00d4ff, #00b8e6)', boxShadow: '0 0 40px rgba(0,212,255,0.3)' }}
              >
                Umów konsultację
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/realizacje"
                className="inline-flex items-center justify-center gap-2 text-white/70 hover:text-white px-10 py-5 rounded-xl font-bold text-lg whitespace-nowrap cursor-pointer border border-white/15 hover:border-white/30 transition-all"
              >
                Zobacz realizacje
                <i className="ri-gallery-line" />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
}
